// ── Delete a client from the roster: one with an undo toast, or several at once ──
// MOVED from app.js (2026-10-23, standing rule 3: code in app.js that changes moves out first, in its own
// commit). app.js keeps the roster itself (its client list, the rows hidden while a delete waits, the rows ticked, the
// table's paint) and hands them over through setClientDeleteDeps; window.rosterDelete and window.bulkDelete still name
// these two for the roster's buttons.

import { getMonthlyRecurring, formatCurrency, escHtml, showToast } from './utils.js';
import { deleteClient } from './clients.js';
import { showConfirmDialog, confirmDelete, clearCardPayments } from './modals.js';
import { cancelSubscriptions, cardPaymentsWaiting } from './cancel-client-subscriptions.js';

/** app.js's own roster, handed over as record-payment.js and charge-oneoff.js take it. */
let _deps = {
  getClients: () => /** @type {any[]} */ ([]),
  /** @type {Set<string>} ids hidden while their delete waits on the undo toast */
  pendingDelete: new Set(),
  /** @type {Set<string>} ids ticked in the roster */
  selectedClients: new Set(),
  renderClientsTable: () => {},
};
/** @param {Partial<typeof _deps>} d */
export function setClientDeleteDeps(d) { _deps = { ..._deps, ...d }; }
const clientsList = () => _deps.getClients() || [];

// ── Delete with undo ────────────────────────────────────────────
// A no-subscription client deletes optimistically with a 6s undo toast. Clients
// WITH a Stripe sub route to the explicit confirm + billing warning (never an
// undo toast that would silently cancel billing on a timeout); bulk delete
// itemises the subs it will cancel (see window.bulkDelete).
// ⚠️ INSTALLMENTS STEP 4 (Task A13): a card installment still waiting in Stripe is
// Stripe billing too, on a client with no subscription. The roster asks the server
// first (changing nothing); a client with any goes through the explicit confirm,
// which cancels them before the delete (modals.js clearCardPayments).
function showUndoToast(message, { onUndo, onCommit, duration = 6000 } = {}) {
  let stack = document.getElementById('toast-stack');
  if (!stack) { stack = document.createElement('div'); stack.id = 'toast-stack'; stack.className = 'toast-stack'; document.body.appendChild(stack); }
  const toast = document.createElement('div');
  toast.className = 'toast toast-info toast--undo';
  let settled = false;
  const commit = () => { if (settled) return; settled = true; clearTimeout(timer); toast.remove(); onCommit && onCommit(); };
  const undo   = () => { if (settled) return; settled = true; clearTimeout(timer); toast.remove(); onUndo && onUndo(); };
  const msgEl = document.createElement('span'); msgEl.className = 'toast-msg'; msgEl.textContent = message;
  const btn = document.createElement('button'); btn.type = 'button'; btn.className = 'toast-undo-btn'; btn.textContent = 'Undo';
  btn.addEventListener('click', undo);
  toast.append(msgEl, btn);
  stack.appendChild(toast);
  const timer = setTimeout(commit, duration);
}

export async function rosterDelete(id) {
  const c = clientsList().find(x => x.id === id);
  if (!c) return;
  // Anything touching Stripe gets the explicit confirm + warning.
  if (c.stripeSubscriptionId || c.stripeContinuitySubscriptionId) { confirmDelete(id); return; }
  // Card installments waiting in Stripe count; a server that could not tell sends the client to the confirm too
  const cards = await cardPaymentsWaiting(id);
  if (cards !== 0) { confirmDelete(id, { cards: cards || 0 }); return; }
  _deps.pendingDelete.add(id);
  _deps.renderClientsTable();
  showUndoToast(`Deleted ${c.name}.`, {
    onUndo: () => { _deps.pendingDelete.delete(id); _deps.renderClientsTable(); },
    onCommit: async () => {
      try {
        await deleteClient(id); // realtime sync drops it from `clients`; stays hidden meanwhile
      } catch (e) {
        _deps.pendingDelete.delete(id);
        _deps.renderClientsTable();
        showToast('Delete failed: ' + e.message, 'error');
      }
    },
  });
}

export function bulkDelete() {
  const ids = [..._deps.selectedClients];
  if (!ids.length) return;
  const subset   = ids.map(id => clientsList().find(c => c.id === id)).filter(Boolean);
  const n        = subset.length;
  const withSubs = subset.filter(c => c.stripeSubscriptionId || c.stripeContinuitySubscriptionId);

  // Itemise the clients whose Stripe billing will be cancelled, with amounts, so
  // the coach makes an informed decision — this ALSO fixes a real gap: bulk delete
  // previously deleted the rows but never cancelled their Stripe subs.
  let message = `Delete ${n} client${n === 1 ? '' : 's'}? This can't be undone.`;
  if (withSubs.length) {
    const rows = withSubs.map(c => {
      const amt = getMonthlyRecurring(c);
      return `<li>${escHtml(c.name)}${amt ? ` — ${formatCurrency(amt)}/mo` : ''}</li>`;
    }).join('');
    message = `Deleting ${n} client${n === 1 ? '' : 's'}. <strong>${withSubs.length} `
      + `${withSubs.length === 1 ? 'has an active subscription that will be cancelled' : 'have active subscriptions that will be cancelled'} in Stripe:</strong>`
      + `<ul class="del-sub-list">${rows}</ul>This can't be undone.`;
  }

  showConfirmDialog({
    message,
    okLabel: `Delete ${n} Client${n === 1 ? '' : 's'}`,
    okClass: 'btn-danger',
    onConfirm: async () => {
      // Installments step 4 (Task A13): each client's card installments still waiting in Stripe go first. A client whose
      // card payments did not all go, or who still owes one, is NOT deleted (one at a time asks about what they owe).
      const cardsOut = await Promise.all(subset.map(c => clearCardPayments(c, { alone: false })));
      const cardsKept = subset.filter((c, i) => !cardsOut[i].ok);
      const keptForCards = new Set(cardsKept.map(c => c.id));
      // Cancel Stripe subs first, then delete. A client whose subscription
      // Stripe would not cancel is NOT deleted: deleting them would leave it
      // charging with no client record (modules/cancel-client-subscriptions.js).
      const stillBilling = [];
      const subsToStop = withSubs.filter(c => !keptForCards.has(c.id));
      for (const c of subsToStop) {
        const failed = await cancelSubscriptions([c.stripeSubscriptionId, c.stripeContinuitySubscriptionId]);
        if (failed.length) stillBilling.push(c);
      }
      const kept    = new Set([...stillBilling, ...cardsKept].map(c => c.id));
      const toDelete = ids.filter(id => !kept.has(id));
      const results = await Promise.allSettled(toDelete.map(id => deleteClient(id)));
      const deleted = new Set(toDelete.filter((id, i) => results[i].status === 'fulfilled'));
      const count   = deleted.size;
      // ⚠️ A DELETED DRAFT CANNOT COME BACK (the review, fix 19): card payments are counted only for clients deleted; a
      // client whose card payments went but who was then not deleted (their subscription, or the delete failed) is named
      const cardsGone = subset.reduce((t, c, i) => t + (cardsOut[i].ok && deleted.has(c.id) ? cardsOut[i].cancelled : 0), 0);
      const cardsGoneKept = subset.filter((c, i) => cardsOut[i].ok && cardsOut[i].cancelled > 0 && !deleted.has(c.id));
      const stopped = subsToStop.length - stillBilling.length;
      const subNote = stopped ? `, ${stopped} subscription${stopped === 1 ? '' : 's'} cancelled` : '';
      const cardNote = cardsGone ? `, ${cardsGone} card payment${cardsGone === 1 ? '' : 's'} still to come cancelled` : '';
      showToast(`${count} client${count === 1 ? '' : 's'} deleted${subNote}${cardNote}.`, 'success');
      if (cardsKept.length) {
        showToast(`${cardsKept.map(c => c.name).join(', ')} ${cardsKept.length === 1 ? 'was' : 'were'} not deleted: their card payments in Stripe couldn't all be cancelled, or they still owe one. Delete them one at a time to see why.`, 'error', { duration: 20000 });
      }
      if (stillBilling.length) {
        showToast(`${stillBilling.map(c => c.name).join(', ')} ${stillBilling.length === 1 ? 'was' : 'were'} not deleted: Stripe would not cancel their subscription. Delete them one at a time to see why.`, 'error', { duration: 20000 });
      }
      // Stripe no longer lets FitOps into the account (the review, fix 22): each client deleted with card payments still
      // there, named in the server's words for the coach to delete them in their own Stripe
      subset.forEach((c, i) => {
        if (cardsOut[i].ok && cardsOut[i].notReached && deleted.has(c.id)) showToast(`${c.name} deleted. ${cardsOut[i].notReached}`, 'error', { duration: 20000 });
      });
      if (cardsGoneKept.length) {
        const names = cardsGoneKept.map(c => c.name);
        const one = names.length === 1;
        showToast(`${one ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`} ${one ? 'was' : 'were'} not deleted, but their card payments still to come were already cancelled in Stripe, so they won't be charged. `
          + `Delete ${one ? names[0] : 'those clients'} again to finish.`, 'error', { duration: 20000 });
      }
      window.clearBulkSelection();
    },
  });
}
